'use client';

/**
 * TaskQueueProvider
 * 
 * Initializes and manages the client task queue polling for web platform.
 * Registers native web skill handlers and starts/stops polling based on auth state.
 */

import { useEffect, useState } from 'react';
import { clientTaskQueueService } from '@/lib/services/client-task-queue-service';
import { createClient } from '@/lib/supabase/client';
import type { User } from '@supabase/supabase-js';

// Register web-specific skill handlers
function registerWebSkillHandlers() {
    // Browser notification
    clientTaskQueueService.registerSkillHandler('browser_notification', async (task) => {
        if (!('Notification' in window)) {
            return { success: false, error: 'Notifications not supported' };
        }

        if (Notification.permission !== 'granted') {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                return { success: false, error: 'Notification permission denied' };
            }
        }

        new Notification('LinkBox', {
            body: task.command,
            icon: '/icon-192.png'
        });
        return { success: true, response: 'Notification shown' };
    });

    // Clipboard operations
    clientTaskQueueService.registerSkillHandler('clipboard_copy', async (task) => {
        try {
            const text = task.parameters?.text as string || task.command;
            await navigator.clipboard.writeText(text);
            return { success: true, response: 'Copied to clipboard' };
        } catch (e: any) {
            return { success: false, error: `Clipboard error: ${e.message}` };
        }
    });

    clientTaskQueueService.registerSkillHandler('clipboard_read', async (task) => {
        try {
            const text = await navigator.clipboard.readText();
            return { success: true, response: text, data: { text } };
        } catch (e: any) {
            return { success: false, error: `Clipboard error: ${e.message}` };
        }
    });

    // Open URL
    clientTaskQueueService.registerSkillHandler('open_url', async (task) => {
        const url = task.parameters?.url as string || task.command;
        window.open(url, '_blank');
        return { success: true, response: `Opened: ${url}` };
    });

    // Share API (if available)
    clientTaskQueueService.registerSkillHandler('share_content', async (task) => {
        if (!navigator.share) {
            return { success: false, error: 'Share API not available' };
        }

        try {
            await navigator.share({
                title: 'LinkBox',
                text: task.command,
                url: task.parameters?.url as string
            });
            return { success: true, response: 'Shared successfully' };
        } catch (e: any) {
            if (e.name === 'AbortError') {
                return { success: true, response: 'Share cancelled' };
            }
            return { success: false, error: e.message };
        }
    });

    // Browser vibration (if available)
    clientTaskQueueService.registerSkillHandler('haptic_feedback', async (task) => {
        if (!navigator.vibrate) {
            return { success: false, error: 'Vibration not supported' };
        }
        navigator.vibrate(100);
        return { success: true, response: 'Vibration triggered' };
    });

    // Speech via LinkBox Voice (agentic bidirectional voice — NO browser TTS)
    clientTaskQueueService.registerSkillHandler('speak_text', async (task) => {
        try {
            const { linkVoiceWebSocket, ConnectionState } = await import('@/lib/voice-engine/link-voice-websocket');

            // If already connected, just send the text
            if (linkVoiceWebSocket.connectionState === ConnectionState.CONNECTED) {
                linkVoiceWebSocket.sendTextMessage(task.command);
            } else {
                // Connect and send
                linkVoiceWebSocket.configure({
                    voiceName: 'Kore',
                    systemInstruction: 'You are Link, the LinkBox AI agent. Speak the following message naturally.',
                    enableTranscription: true,
                });
                await linkVoiceWebSocket.connect();
                linkVoiceWebSocket.sendTextMessage(task.command);
            }
            return { success: true, response: `Speaking via Link Voice: ${task.command.substring(0, 50)}...` };
        } catch (e: any) {
            return { success: false, error: `Link Voice error: ${e.message}` };
        }
    });

    // Geolocation
    clientTaskQueueService.registerSkillHandler('get_location', async (task) => {
        if (!navigator.geolocation) {
            return { success: false, error: 'Geolocation not supported' };
        }

        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        success: true,
                        response: `📍 Location: ${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`,
                        data: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        }
                    });
                },
                (error) => {
                    resolve({ success: false, error: error.message });
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        });
    });

    // Battery status (if available)
    clientTaskQueueService.registerSkillHandler('get_battery', async (task) => {
        if (!('getBattery' in navigator)) {
            return { success: false, error: 'Battery API not supported' };
        }

        try {
            const battery = await (navigator as any).getBattery();
            const level = Math.round(battery.level * 100);
            const charging = battery.charging ? 'Charging' : 'Unplugged';

            return {
                success: true,
                response: `🔋 Battery: ${level}% (${charging})`,
                data: { level, charging: battery.charging }
            };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    });

    // Local storage operations
    clientTaskQueueService.registerSkillHandler('storage_set', async (task) => {
        const key = task.parameters?.key as string;
        const value = task.parameters?.value as string || task.command;

        if (!key) {
            return { success: false, error: 'Missing key parameter' };
        }

        localStorage.setItem(`linkbox_${key}`, value);
        return { success: true, response: `Stored: ${key}` };
    });

    clientTaskQueueService.registerSkillHandler('storage_get', async (task) => {
        const key = task.parameters?.key as string || task.command;
        const value = localStorage.getItem(`linkbox_${key}`);

        if (value === null) {
            return { success: false, error: `Key not found: ${key}` };
        }

        return { success: true, response: value, data: { key, value } };
    });

    // Download file
    clientTaskQueueService.registerSkillHandler('download_file', async (task) => {
        const url = task.parameters?.url as string;
        const filename = task.parameters?.filename as string || 'download';

        if (!url) {
            return { success: false, error: 'Missing URL parameter' };
        }

        try {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            return { success: true, response: `Download started: ${filename}` };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    });

    console.log('✅ [TaskQueue] Registered 12 native web skill handlers');
}

export function TaskQueueProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Listen for auth state changes
    useEffect(() => {
        const supabase = createClient();

        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setIsLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
            setIsLoading(false);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (isLoading) return;

        if (user) {
            // User is authenticated - start polling
            console.log('📱 [TaskQueue] User authenticated, starting polling...');
            registerWebSkillHandlers();
            clientTaskQueueService.startPolling(5000); // Poll every 5 seconds
        } else {
            // User logged out - stop polling
            console.log('📱 [TaskQueue] User not authenticated, stopping polling...');
            clientTaskQueueService.stopPolling();
        }

        // Cleanup on unmount
        return () => {
            clientTaskQueueService.stopPolling();
        };
    }, [user, isLoading]);

    // Handle visibility changes to pause/resume polling
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log('📱 [TaskQueue] Tab hidden, stopping polling');
                clientTaskQueueService.stopPolling();
            } else if (user) {
                console.log('📱 [TaskQueue] Tab visible, resuming polling');
                clientTaskQueueService.startPolling(5000);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user]);

    return <>{children}</>;
}

